import React from "react";

import type { DashboardData } from "../../types";
import { formatINR, formatMarginPct } from "../../utils/format";
import "./EventKpiStrip.css";

type KpiTone = "profit" | "loss" | "neutral";

function kpiValueClass(tone: KpiTone): string {
  switch (tone) {
    case "profit":
      return "app-shell-kpi-value app-shell-kpi-value--profit";
    case "loss":
      return "app-shell-kpi-value app-shell-kpi-value--loss";
    default:
      return "app-shell-kpi-value app-shell-kpi-value--neutral";
  }
}

/**
 * Derived KPI figures shared by the Overview and P&L tabs.
 *
 * Exported so the pages can reuse the same numbers in their charts without
 * recomputing them (and drifting from the strip).
 */
export function deriveEventKpis(data: DashboardData) {
  const review = data.review;
  const projectedRevenue = Number(data.metrics.projected_revenue) || 0;
  const totalCost = Number(data.metrics.total_cost) || 0;
  const projectedProfit = Number(data.metrics.profit ?? projectedRevenue - totalCost) || 0;
  const actualRevenue = Number(
    review?.total_revenue ?? data.metrics.actual_revenue ?? data.ticket_summary.revenue_generated ?? 0
  );
  const actualProfit = Number(
    review?.net_profit ?? data.metrics.actual_profit ?? actualRevenue - totalCost
  );
  // Preserve null when the backend reports an undefined margin (revenue = 0).
  // Computing actualProfit/0 locally would re-introduce the misleading 0.0%
  // the backend deliberately avoids.
  const actualProfitMargin: number | null = (() => {
    const raw = data.metrics.actual_profit_margin;
    if (raw === null || raw === undefined) {
      return actualRevenue > 0 ? (actualProfit / actualRevenue) * 100 : null;
    }
    return Number(raw);
  })();

  return {
    review,
    projectedRevenue,
    totalCost,
    projectedProfit,
    actualRevenue,
    actualProfit,
    actualProfitMargin,
  };
}

type Props = {
  data: DashboardData;
};

/**
 * The six-metric strip the Sprint 14 wireframes place at the top of both the
 * Overview and P&L tabs: actual revenue / profit / margin, projected revenue
 * / profit, total cost.
 */
export const EventKpiStrip: React.FC<Props> = ({ data }) => {
  const {
    review,
    projectedRevenue,
    totalCost,
    projectedProfit,
    actualRevenue,
    actualProfit,
    actualProfitMargin,
  } = deriveEventKpis(data);

  const revenueTone: KpiTone = actualRevenue > 0 ? "profit" : "neutral";
  const costTone: KpiTone = totalCost > 0 ? "loss" : "neutral";
  const profitTone: KpiTone = actualProfit > 0 ? "profit" : actualProfit < 0 ? "loss" : "neutral";
  const marginTone: KpiTone =
    actualProfitMargin === null
      ? "neutral"
      : actualProfitMargin > 0
        ? "profit"
        : actualProfitMargin < 0
          ? "loss"
          : "neutral";
  const projectedRevenueTone: KpiTone = projectedRevenue > 0 ? "profit" : "neutral";
  const projectedProfitTone: KpiTone =
    projectedProfit > 0 ? "profit" : projectedProfit < 0 ? "loss" : "neutral";

  return (
    <div className="app-shell-kpi-strip event-kpi-strip">
      <div className="app-shell-kpi">
        <span className="app-shell-kpi-label">Actual revenue</span>
        <span className={kpiValueClass(revenueTone)}>{formatINR(actualRevenue)}</span>
        <span className="app-shell-kpi-sub">{review ? "From sales · live" : "Sum of tier sales"}</span>
      </div>
      <div className="app-shell-kpi">
        <span className="app-shell-kpi-label">Actual profit</span>
        <span className={kpiValueClass(profitTone)}>{formatINR(actualProfit)}</span>
        <span className="app-shell-kpi-sub">Revenue − cost</span>
      </div>
      <div className="app-shell-kpi">
        <span className="app-shell-kpi-label">Actual margin</span>
        <span className={kpiValueClass(marginTone)}>
          {formatMarginPct(actualProfitMargin, actualRevenue)}
        </span>
        <span className="app-shell-kpi-sub">Profit / actual revenue</span>
      </div>
      <div className="app-shell-kpi">
        <span className="app-shell-kpi-label">Projected revenue</span>
        <span className={kpiValueClass(projectedRevenueTone)}>{formatINR(projectedRevenue)}</span>
        <span className="app-shell-kpi-sub">At full sell-through</span>
      </div>
      <div className="app-shell-kpi">
        <span className="app-shell-kpi-label">Projected profit</span>
        <span className={kpiValueClass(projectedProfitTone)}>{formatINR(projectedProfit)}</span>
        <span className="app-shell-kpi-sub">Projected − cost</span>
      </div>
      <div className="app-shell-kpi">
        <span className="app-shell-kpi-label">Total cost</span>
        <span className={kpiValueClass(costTone)}>{formatINR(totalCost)}</span>
        <span className="app-shell-kpi-sub">Loaded costs</span>
      </div>
    </div>
  );
};

export default EventKpiStrip;
